import { useId, useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultZIndexes,
  LabelList,
  Legend,
  type LegendPayload,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { ActualRow, TargetRow } from '../../types/budget-actual.js';
import {
  BUDGET_ACTUAL_COLOR,
  CHART_COLORS,
  CHART_COLORS_EXTENDED,
  OFFER_UPLIFT_TINT,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_PROPS,
} from '../../lib/chart-theme.js';
import { formatPercent } from '../../lib/revenue-aggregation.js';

/**
 * チャート1行分のデータ点。
 * @graph-connects none
 */
interface DataPoint {
  label: string;
  target: number;
  actual: number | null;
  achievementRate: number | null;
  /** 実績（オファーあり）。積み上げの天辺。積み上げないカードは null */
  withOffer: number | null;
  /** オファーによる上乗せ（オファーあり − オファー抜き）。積み上げないカードは null */
  uplift: number | null;
  /**
   * 上段が見えない（上乗せが無い・0・負）ときに、実績（オファー抜き）の棒の天辺に出す数字（＝オファー抜き）。
   * 上段があるときは天辺にオファーありを出すので null。積み上げないカードも null
   */
  actualTop: number | null;
  /**
   * 上段がある（上乗せが正）ときだけ、実績（オファー抜き）の段の中に出す数字（＝オファー抜き）。
   * 上段が無い単位は天辺に同じ数字を出すので null（同じ数字を2回出さない）。積み上げないカードも null
   */
  actualInside: number | null;
}

/**
 * 実績の棒を「オファー抜き」の下段と「オファーによる上乗せ」の上段に積むときの設定。ページが渡す
 * （どのカードを積むかはページが決め、チャートは指標キーを直接知らない）。
 * @graph-connects none
 */
export interface OfferStackConfig {
  /** 下段（実績・オファー抜き）の指標キー。 */
  lowerKey: string;
  /** 天辺（実績・オファーあり）の指標キー。上段＝オファーあり − オファー抜き。 */
  withOfferKey: string;
}

/** @graph-connects none */
const TARGET_COLOR = CHART_COLORS[0];
/** @graph-connects none */
const ACTUAL_COLOR = BUDGET_ACTUAL_COLOR;
/** @graph-connects none */
const ACHIEVEMENT_COLOR = CHART_COLORS_EXTENDED[5];

/**
 * 積み上げのカードの凡例の並び（dataKey 順）。recharts の既定は名前の文字順なので明示する。
 * @graph-connects none
 */
const OFFER_STACK_LEGEND_ORDER: readonly string[] = [
  'target',
  'actual',
  'uplift',
  'achievementRate',
];

/** 棒の中の数字を出す最小の段の高さ（px）。縦に置いた数字が段に収まらないときは出さない。 @graph-connects none */
const INSIDE_LABEL_MIN_HEIGHT = 44;

/** @graph-connects none */
function formatNum(v: number): string {
  return v.toLocaleString('ja-JP');
}

/** 整数に丸めて桁区切りにする（積み上げのカードは小数を出さない）。 @graph-connects none */
function formatInt(v: number): string {
  return Math.round(v).toLocaleString('ja-JP');
}

/**
 * 数字の縁取り（カードの背景色）。達成率の折れ線の点が数字に重なっても、数字の周りだけ点を隠して読めるようにする
 * （SVG の paint-order で縁取りを先に塗り、文字を上に重ねる）。
 * @graph-connects none
 */
const LABEL_HALO = {
  stroke: 'var(--color-bg-surface)',
  strokeWidth: 3,
  strokeLinejoin: 'round',
  paintOrder: 'stroke',
} as const;

/**
 * 棒の天辺の数値ラベル。数の書式は既定で桁区切り（小数はそのまま）、積み上げのカードは整数を渡す。
 * 値が無い・0 なら出さない。halo を渡したときだけ、背景色の縁取りを付ける（積み上げのカードだけ。ほかのカードの
 * 見た目は変えない）。
 * @graph-connects LABEL_HALO [references] 背景色の縁取り
 */
function BarLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  value?: number | null;
  format?: (v: number) => string;
  halo?: boolean;
}) {
  const { x = 0, y = 0, width = 0, value, format = formatNum, halo = false } = props;
  if (value === undefined || value === null || value === 0) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 4}
      textAnchor="middle"
      fontSize={10}
      fill="var(--color-text-muted)"
      {...(halo ? LABEL_HALO : {})}
    >
      {format(value)}
    </text>
  );
}

/**
 * 積み上げのカードの目標の棒の天辺の数字。BarLabel に整数の書式だけを渡す（縁取りは付けず、層も上げない。
 * 目標の数字の上には、ほかのカードと同じく達成率の点が乗る）。
 * @graph-connects BarLabel [calls] 天辺の数値ラベル
 */
function intTargetLabel(props: { x?: number; y?: number; width?: number; value?: number | null }) {
  return <BarLabel {...props} format={formatInt} />;
}

/**
 * 積み上げのカードの実績の棒の天辺の数字（実績（オファーあり）、上段が無いときは実績（オファー抜き））。
 * BarLabel に整数の書式と背景色の縁取りを渡す（天辺の数字に達成率の点が重なっても読めるように）。
 * @graph-connects BarLabel [calls] 天辺の数値ラベル
 */
function intTopLabel(props: { x?: number; y?: number; width?: number; value?: number | null }) {
  return <BarLabel {...props} format={formatInt} halo />;
}

/**
 * 積み上げのカードの下の段（実績（オファー抜き））の中の数字。棒の幅に収まるよう縦に置き、
 * 段が低くて収まらないときは出さない。上段がある単位だけに出す（上段が無い単位は天辺に出す）。
 * @graph-connects none
 */
function InsideIntLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number | null;
}) {
  const { x = 0, y = 0, width = 0, height = 0, value } = props;
  if (value === undefined || value === null || value === 0 || height < INSIDE_LABEL_MIN_HEIGHT) {
    return null;
  }
  const cx = x + width / 2;
  const cy = y + height / 2;
  return (
    <text
      x={cx}
      y={cy}
      transform={`rotate(-90 ${String(cx)} ${String(cy)})`}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10}
      fill="#FFFFFF"
    >
      {formatInt(value)}
    </text>
  );
}

/**
 * 積み上げのカードの吹き出しに出す系列名。
 * @graph-connects none
 */
interface OfferCpaTooltipLabels {
  target: string;
  actual: string;
  withOffer: string;
  uplift: string;
  achievementRate: string;
}

/**
 * 積み上げのカードの吹き出し。目標・オファー抜き・オファーあり・上乗せ・達成率を並べ、値が無い行は「—」。
 * 既定の吹き出しは値が null の行を消すため、行を自前で並べる。
 * @graph-connects none
 */
function OfferCpaTooltip({
  active,
  payload,
  label,
  labels,
}: Pick<TooltipContentProps, 'active' | 'payload' | 'label'> & {
  labels: OfferCpaTooltipLabels;
}) {
  const point = payload[0]?.payload as DataPoint | undefined;
  if (!active || point === undefined) return null;
  const rows: { name: string; value: string; color: string }[] = [
    { name: labels.target, value: formatInt(point.target), color: TARGET_COLOR },
    {
      name: labels.actual,
      value: point.actual === null ? '—' : formatInt(point.actual),
      color: ACTUAL_COLOR,
    },
    {
      name: labels.withOffer,
      value: point.withOffer === null ? '—' : formatInt(point.withOffer),
      color: ACTUAL_COLOR,
    },
    {
      name: labels.uplift,
      value: point.uplift === null ? '—' : formatInt(point.uplift),
      color: ACTUAL_COLOR,
    },
    {
      name: labels.achievementRate,
      value: point.achievementRate === null ? '—' : `${point.achievementRate.toFixed(1)}%`,
      color: ACHIEVEMENT_COLOR,
    },
  ];
  return (
    <div style={{ ...TOOLTIP_CONTENT_STYLE, margin: 0, padding: 10, whiteSpace: 'nowrap' }}>
      <p style={{ margin: 0 }}>{label}</p>
      <ul style={{ padding: 0, margin: 0 }}>
        {rows.map((row) => (
          <li
            key={row.name}
            style={{ display: 'block', paddingTop: 4, paddingBottom: 4, color: row.color }}
          >
            {row.name} : {row.value}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 積み上げのカードの凡例の並び順（目標 → オファー抜き → 上乗せ → 達成率）。並びに無い系列は最後に回す。
 * @graph-connects none
 */
function offerStackLegendOrder(item: LegendPayload): number {
  const index = OFFER_STACK_LEGEND_ORDER.indexOf(String(item.dataKey));
  return index === -1 ? OFFER_STACK_LEGEND_ORDER.length : index;
}

/**
 * 積み上げのカードの凡例の文字。斜線（pattern）で塗る系列は色が url(...) になるため、実績の色で出す。
 * @graph-connects none
 */
function offerStackLegendText(value: string, entry: LegendPayload) {
  const color = entry.color ?? '';
  return <span style={{ color: color.startsWith('url(') ? ACTUAL_COLOR : color }}>{value}</span>;
}

/** @graph-connects none */
const LOWER_IS_BETTER_KEYS = new Set([
  'subChurned',
  'activeChurned',
  'inactiveChurned',
  'cpaTotalVal',
  'cpaOps',
  'cpaDirect',
  'cpaAlliance',
  'cpaReferral',
  'cpaDigital',
  'cpaOpsOnly',
  'adCostTotal',
  'adCostOps',
  'adCostOther',
  'costDirect',
  'costAlliance',
  'costReferral',
  'costDigital',
  'costOpsTotal',
  'costOther',
  'inquiryRate',
  'resolveTime',
  'inquiryCount',
]);

/** @graph-connects none */
const RATE_KEYS = new Set([
  'channelRatioDirect',
  'channelRatioReferral',
  'channelRatioAlliance',
  'channelRatioDigital',
  'firstMonthRate',
  'onboardRate',
  'inquiryRate',
]);

/** @graph-connects none */
const DECIMAL_KEYS = new Set(['firstRating', 'resolveTime']);

interface TargetChartProps {
  title: string;
  metricKey: string;
  rows: TargetRow[];
  actuals?: ActualRow[];
  hasActual?: boolean;
  /** 実績の棒を積むときの設定。渡したカードだけ積む（渡さなければ今までどおり1本の棒）。 */
  offerStack?: OfferStackConfig;
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 汎用予実チャート。目標値と実績値を棒グラフで並べて表示し、進捗達成率を右Y軸の折れ線（100%基準線付き）で重畳する。hasActual=true の指標のみ実績バーと達成率ラインを表示。LOWER_IS_BETTER_KEYS に該当する指標（churn・CPA・コスト・問い合わせ等）は達成率を target/actual で算出し、100%超＝目標達成と正しく表現する。ページが積み上げの設定（下段・オファーありの指標キー）を渡したカード（合計CPA）だけは、実績の棒を「オファー抜き」と「オファーによる上乗せ（斜線）」の2段に積み、上段がある単位は天辺にオファーあり・段の中にオファー抜きの整数を、上段が無い（オファーありが無い・上乗せが 0 か負）単位は下段の天辺にオファー抜きの整数だけを出す（どの単位でも数字は1つ以上、同じ数字は2回出さない。達成率はオファー抜きで計算）。天辺の数字は BarLabel に書式を渡して出し、積み上げのカードの実績の天辺の数字だけ背景色で縁取って達成率の折れ線・点より前面に描く（点が重なっても数字が欠けて見えないように。目標の数字とほかのカードの見た目・層は変えない）。
 * @graph-connects formatPercent [calls] 割合の目盛り・吹き出しの書式（オファーの内訳と共通）
 */
export function TargetChart({
  title,
  metricKey,
  rows,
  actuals,
  hasActual = false,
  offerStack,
}: TargetChartProps) {
  const { t } = useTranslation();
  const isRate = RATE_KEYS.has(metricKey);
  const isDecimal = DECIMAL_KEYS.has(metricKey);
  const showActual = hasActual && actuals && actuals.length > 0;
  const stackOffer = showActual === true && offerStack !== undefined;
  // 積むときは下段の実績を設定の指標キーから読む（目標は今までどおり metricKey）
  const actualKey = stackOffer ? offerStack.lowerKey : metricKey;
  // useId の値は記号を含みうるので、SVG の url(#id) で参照できる文字だけにする
  const hatchId = `offer-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const data = useMemo((): DataPoint[] => {
    return rows.map((row, i) => {
      const targetValue = (row as unknown as Record<string, number | null>)[metricKey];
      const actualRow = actuals?.[i];
      const actualValues =
        showActual && actualRow ? (actualRow as unknown as Record<string, number | null>) : null;
      const actualValue = actualValues === null ? null : actualValues[actualKey];
      const target =
        typeof targetValue === 'number' && Number.isFinite(targetValue) ? targetValue : 0;
      const actual =
        typeof actualValue === 'number' && Number.isFinite(actualValue) ? actualValue : null;
      const achievementRate =
        actual !== null && target > 0
          ? LOWER_IS_BETTER_KEYS.has(metricKey)
            ? actual > 0
              ? Math.round((target / actual) * 1000) / 10
              : null
            : Math.round((actual / target) * 1000) / 10
          : null;
      const withOfferValue =
        stackOffer && actualValues !== null ? actualValues[offerStack.withOfferKey] : null;
      const withOffer =
        actual !== null && typeof withOfferValue === 'number' && Number.isFinite(withOfferValue)
          ? withOfferValue
          : null;
      // 上乗せはオファーあり − オファー抜き。どちらかが無い・負（オファーありが同じ日の集合で出ていない）なら
      // 上段を描かない（積み上げが下段の中へ食い込むのを防ぐ）
      const uplift =
        withOffer !== null && actual !== null && withOffer >= actual ? withOffer - actual : null;
      // 上段が無い・高さ 0（recharts は高さ 0 の段を描かず、その段の天辺の数字も出さない）ときは、
      // 下段の天辺にオファー抜きを出す。段の中の数字は低い棒では出ないので、これが無いと数字が1つも見えない
      const actualTop = stackOffer && (uplift === null || uplift === 0) ? actual : null;
      // 段の中の数字は上段がある単位だけ。上段が無い単位は天辺に同じ数字を出すので、中には出さない
      const actualInside = stackOffer && uplift !== null && uplift > 0 ? actual : null;
      return {
        label: row.label,
        target,
        actual,
        achievementRate,
        withOffer,
        uplift,
        actualTop,
        actualInside,
      };
    });
  }, [rows, actuals, metricKey, actualKey, showActual, stackOffer, offerStack]);

  if (data.length === 0) return null;

  const maxTarget = Math.max(...data.map((d) => d.target), 0);
  // 積み上げのカードは、オファーあり・オファー抜きの大きい方まで縦軸に入れる
  // （オファーありの方が小さい単位は上段を描かず下段だけを描くので、下段が切れないようにする）
  const maxActual = showActual
    ? Math.max(
        ...data.map((d) =>
          stackOffer ? Math.max(d.withOffer ?? 0, d.actual ?? 0) : (d.actual ?? 0),
        ),
        0,
      )
    : 0;
  const maxVal = Math.max(maxTarget, maxActual);
  const yDomain: [number, number] = [0, Math.ceil(maxVal * 1.15) || 1];

  const tickFormatter = isRate
    ? (v: number) => formatPercent(v)
    : isDecimal
      ? (v: number) => v.toFixed(1)
      : stackOffer
        ? (v: number) => formatInt(v)
        : (v: number) => formatNum(v);

  const hasAchievement = showActual && data.some((d) => d.achievementRate !== null);
  const maxAchievement = hasAchievement
    ? Math.max(...data.map((d) => d.achievementRate ?? 0), 0)
    : 0;
  const rightDomain: [number, number] = [0, Math.ceil(Math.max(maxAchievement * 1.15, 120))];

  const tooltipFormatter = (value: number | null, name: string): [string, string] => {
    if (value === null) return ['—', name];
    if (name === t('budgetActual.achievementRate')) return [`${value.toFixed(1)}%`, name];
    if (isRate) return [formatPercent(value), name];
    if (isDecimal) return [value.toFixed(2), name];
    return [formatNum(value), name];
  };

  const offerTooltipLabels: OfferCpaTooltipLabels = {
    target: t('budgetActual.target'),
    actual: t('budgetActual.actualWithoutOffer'),
    withOffer: t('budgetActual.actualWithOffer'),
    uplift: t('budgetActual.offerUplift'),
    achievementRate: t('budgetActual.achievementRate'),
  };

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 40, bottom: 4, left: 8 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
            <YAxis
              yAxisId="left"
              domain={yDomain}
              tick={AXIS_TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={tickFormatter}
            />
            {hasAchievement && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={rightDomain}
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `${String(v)}%`}
              />
            )}
            {stackOffer ? (
              <Tooltip
                content={(props: TooltipContentProps) => (
                  <OfferCpaTooltip
                    active={props.active}
                    payload={props.payload}
                    label={props.label}
                    labels={offerTooltipLabels}
                  />
                )}
              />
            ) : (
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={tooltipFormatter as never} />
            )}
            {stackOffer ? (
              <Legend
                {...LEGEND_PROPS}
                itemSorter={offerStackLegendOrder}
                formatter={offerStackLegendText}
              />
            ) : (
              <Legend {...LEGEND_PROPS} />
            )}
            {hasAchievement && (
              <ReferenceLine
                yAxisId="right"
                y={100}
                stroke="var(--color-border)"
                strokeDasharray="4 4"
                label={false}
              />
            )}
            {stackOffer && (
              <defs>
                <pattern
                  id={hatchId}
                  patternUnits="userSpaceOnUse"
                  width="5"
                  height="5"
                  patternTransform="rotate(45)"
                >
                  <rect width="5" height="5" fill={OFFER_UPLIFT_TINT} />
                  <rect width="2" height="5" fill={ACTUAL_COLOR} />
                </pattern>
              </defs>
            )}
            {stackOffer ? (
              // 積み上げの棒は積まない棒より先（左）に描かれるので、目標にも自分だけの積み上げを持たせて左に置く
              <Bar
                yAxisId="left"
                dataKey="target"
                stackId="target"
                name={t('budgetActual.target')}
                fill={TARGET_COLOR}
                radius={[2, 2, 0, 0]}
              >
                {/* 目標の数字は縁取りを付けず、層も上げない（達成率の点がこれまでどおり上に乗る） */}
                <LabelList dataKey="target" content={intTargetLabel as never} />
              </Bar>
            ) : (
              <Bar
                yAxisId="left"
                dataKey="target"
                name={t('budgetActual.target')}
                fill={TARGET_COLOR}
                radius={[2, 2, 0, 0]}
                label={BarLabel as never}
              />
            )}
            {stackOffer && (
              <Bar
                yAxisId="left"
                dataKey="actual"
                stackId="actual"
                name={t('budgetActual.actualWithoutOffer')}
                fill={ACTUAL_COLOR}
              >
                <LabelList dataKey="actualInside" content={InsideIntLabel as never} />
                {/* 実績の天辺の数字だけ、達成率の折れ線・点より前面の層に描く */}
                <LabelList
                  dataKey="actualTop"
                  content={intTopLabel as never}
                  zIndex={DefaultZIndexes.label}
                />
              </Bar>
            )}
            {stackOffer && (
              <Bar
                yAxisId="left"
                dataKey="uplift"
                stackId="actual"
                name={t('budgetActual.offerUplift')}
                fill={`url(#${hatchId})`}
                radius={[2, 2, 0, 0]}
              >
                <LabelList
                  dataKey="withOffer"
                  content={intTopLabel as never}
                  zIndex={DefaultZIndexes.label}
                />
              </Bar>
            )}
            {showActual && !stackOffer && (
              <Bar
                yAxisId="left"
                dataKey="actual"
                name={t('budgetActual.actual')}
                fill={ACTUAL_COLOR}
                radius={[2, 2, 0, 0]}
                label={BarLabel as never}
              />
            )}
            {hasAchievement && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="achievementRate"
                name={t('budgetActual.achievementRate')}
                stroke={ACHIEVEMENT_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: ACHIEVEMENT_COLOR }}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
